import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import React from 'react';
import { json } from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { buildMeta } from '~/lib/seo';
import { routes } from '~/lib/routes';
import { H1, H2, Caption, Lead } from '~/components/ui/typography';
import { Container } from '~/components/ui/container';

interface ClassSummary {
  id: string;
  name: string;
  role: string;
}

export const loader = async (_args: LoaderFunctionArgs) => {
  const classes: ClassSummary[] = [];
  return json({ classes });
};

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'Classes Database',
    description: 'Discover class archetypes, roles, and progression paths in Lumora.',
  });

export default function ClassesIndexPage() {
  const { classes } = useLoaderData<typeof loader>();

  return (
    <Container size="default" padding="large" className="space-y-6">
      <header className="space-y-3">
        <Caption className="text-primary">Database</Caption>
        <H1>Classes</H1>
        <Lead>
          Specializations, combat roles, and progression paths for playable characters.
        </Lead>
      </header>
      {classes.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-muted-foreground">
          Class data is on the roadmap for Sprint 1.4.
        </p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {classes.map((klass) => (
            <li key={klass.id}>
              <Link
                to={routes.database.classes(klass.id)}
                className="block rounded-lg border border-border bg-card p-4 shadow hover:border-primary/30"
              >
                <H2>{klass.name}</H2>
                <p className="text-sm text-muted-foreground">Primary role: {klass.role}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Container>
  );
}
